import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, RefreshCw, MoreVertical,
  Users, Shield, UserCheck, UserX, Key
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';

const roles = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-700', desc: 'Full access to everything' },
  { value: 'admin', label: 'Admin', color: 'bg-orange-100 text-orange-700', desc: 'Manage content and users' },
  { value: 'editor', label: 'Editor', color: 'bg-blue-100 text-blue-700', desc: 'Create and edit content' },
  { value: 'staff', label: 'Staff', color: 'bg-slate-100 text-slate-700', desc: 'View and basic actions' },
];

const defaultUser = {
  username: '', password: '', name: '', email: '', role: 'staff'
};

export const UsersManager = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await apiService.getUsers(token);
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.username || !item.name || (item.isNew && !item.password)) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      if (item.isNew) {
        await apiService.createUser(token, item);
        toast.success('User created successfully!');
      } else {
        await apiService.updateUser(token, item.id, item);
        toast.success('User updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving user');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteUser(token, deleteDialog.item.id);
      toast.success('User deleted');
      setDeleteDialog({ open: false, item: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const toggleActive = async (user) => {
    try {
      await apiService.updateUser(token, user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultUser, isNew: true }
    });
  };

  const getRoleConfig = (role) => roles.find(r => r.value === role) || roles[3];
  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="users-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500">Manage admin users and their permissions</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => openEditModal()} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="w-4 h-4 mr-2" /> Add User
          </Button>
        )}
      </div>

      {/* Role Legend */}
      <Card>
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-3">Role Permissions:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {roles.map(role => (
              <div key={role.value} className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs rounded ${role.color}`}>{role.label}</span>
                <span className="text-xs text-slate-500">{role.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : users.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No users found</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => {
            const roleConfig = getRoleConfig(user.role);
            const isCurrentUser = user.username === currentUser?.username;
            return (
              <Card key={user.id} className={`${!user.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${user.is_active ? 'bg-gradient-to-br from-sky-500 to-sky-600' : 'bg-slate-400'}`}>
                        {user.name?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          {user.name}
                          {isCurrentUser && <span className="text-xs text-sky-600">(You)</span>}
                        </h3>
                        <p className="text-sm text-slate-500">@{user.username}</p>
                        {user.email && <p className="text-xs text-slate-400">{user.email}</p>}
                      </div>
                    </div>
                    {isSuperAdmin && !isCurrentUser && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="w-8 h-8"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditModal(user)}><Pencil className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleActive(user)}>
                            {user.is_active ? <><UserX className="w-4 h-4 mr-2" /> Deactivate</> : <><UserCheck className="w-4 h-4 mr-2" /> Activate</>}
                          </DropdownMenuItem>
                          {user.role !== 'super_admin' && (
                            <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: user })} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded ${roleConfig.color}`}>{roleConfig.label}</span>
                    {!user.is_active && <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">Inactive</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New User' : 'Edit User'}</DialogTitle>
            <DialogDescription>Fill in the user details below</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={editModal.item.username}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, username: e.target.value } })}
                  placeholder="username"
                  disabled={!editModal.item.isNew}
                />
              </div>

              {editModal.item.isNew && (
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={editModal.item.password || ''}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, password: e.target.value } })}
                    placeholder="Enter password"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={editModal.item.name}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, name: e.target.value } })}
                  placeholder="Full Name"
                />
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editModal.item.email || ''}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, email: e.target.value } })}
                  placeholder="email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editModal.item.role} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, role: value } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${role.color}`}>{role.label}</span>
                          <span className="text-xs text-slate-500">{role.desc}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!editModal.item.isNew && (
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={editModal.item.is_active}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })}
                  />
                  <Label>Active</Label>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-sky-500 hover:bg-sky-600"><Save className="w-4 h-4 mr-2" /> Save User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete user "{deleteDialog.item?.name}"? This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersManager;
